import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Trigger the real emails on demand and watch them land. Nothing here is a simulation. */
export const NotificationsPanel = () => {
  const { getAuthHeader } = useAuth();
  const [f, setF] = useState({ to: '', teen_name: 'Jordan', parent_name: 'Alex Rivera' });
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const load = () => axios.get(`${API}/family/handover-note/log`, getAuthHeader())
    .then(r => setLog(r.data.events)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/family/handover-note/test`, f, getAuthHeader());
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail
        || 'That send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">Admin · notifications</p>
        <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-white mt-2">
          Send it for real, then go look.
        </h1>
        <p className="text-sm text-slate-300 mt-2 max-w-2xl">
          Every email FuturePath sends can be fired at any address from here and is written to the
          email log, so deliverability is something you check rather than hope for.
        </p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-300" />
          <h2 className="font-outfit text-xl font-semibold text-white">Teen handover note</h2>
        </div>
        <p className="text-sm text-slate-300 mt-1.5 max-w-2xl">
          Goes to the parent the moment their 13-17 child sets their own password: what they can
          still do, what changes, and why. Sent automatically on every handover, this button sends
          the same email so you can read it yourself.
        </p>
        <div className="grid sm:grid-cols-4 gap-3 mt-5 items-end">
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300 text-sm">Send to</Label>
            <Input value={f.to} onChange={e => setF({ ...f, to: e.target.value })}
              placeholder="you@yourdomain.com" className="input-dark" data-testid="handover-to" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Teen's name</Label>
            <Input value={f.teen_name} onChange={e => setF({ ...f, teen_name: e.target.value })}
              className="input-dark" data-testid="handover-teen" />
          </div>
          <Button onClick={send} disabled={sending || !f.to.includes('@')} data-testid="handover-send"
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Sending…' : 'Send it now'}
          </Button>
        </div>
      </motion.div>

      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-testid="handover-log">
        <h3 className="font-outfit text-lg font-semibold text-white">Handover notes sent</h3>
        {log.length === 0 ? (
          <p className="text-sm text-slate-300 mt-3">Nothing sent yet.</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {log.map((e, i) => (
              <li key={i} className="text-xs text-slate-300 flex items-center gap-2">
                {e.status === 'success'
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  : <AlertCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                {String(e.created_at).slice(0, 16).replace('T', ' ')} · {e.to}
                {e.type === 'teen_handover_test' && <span className="text-slate-300">test</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
